import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0 active:shadow-card",
        secondary: "bg-background text-foreground border border-border shadow-sm hover:bg-secondary hover:shadow-card-lg hover:-translate-y-0.5 active:translate-y-0",
        destructive: "bg-destructive text-destructive-foreground shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
        outline: "border border-input bg-background shadow-sm hover:bg-secondary hover:border-input hover:shadow-card-lg hover:-translate-y-0.5 active:translate-y-0",
        ghost: "text-muted-foreground hover:bg-secondary hover:text-foreground hover:shadow-sm transition-all",
        link: "text-primary underline-offset-4 hover:underline",
        accent: "bg-accent text-accent-foreground shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
        success: "bg-success text-success-foreground shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
        gradient: "bg-gradient-to-r from-primary via-primary-accent to-accent text-white shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-lg px-8 text-base font-semibold",
        xl: "h-12 rounded-lg px-10 text-base font-semibold",
        icon: "h-10 w-10 min-w-10 min-h-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          variant === 'gradient' && 'button-shine',
          "theme-transition"
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }

